// @ts-nocheck
// Agent Portal (/cti2) — premium "on break" takeover, shown in place of the
// live-call card stack (LiveCallStack.tsx) whenever BreakContext's
// userStatus is 'BREAK'. Nothing like this existed before: the break
// countdown BreakContext already computes (`countdown`, ticking every
// second) was previously wired into props that were destructured and never
// rendered anywhere in the agent-facing screens (dead state) — see
// AgentPortalPage.tsx for how this now consumes it directly instead.
//
import { useEffect, useMemo, useState } from 'react';
import { Coffee, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HAIRLINE } from '../team/teamUi';
import { formatClock } from '../utils/format';

export default function BreakScreen({ breakTitle, breakDescription, breakDuration, countdown, breakExceeded, breakEndTime, onEndBreak, ending }: any) {
  const [overBySeconds, setOverBySeconds] = useState(() =>
    breakEndTime ? Math.max(0, Math.floor((Date.now() - breakEndTime) / 1000)) : 0,
  );

  useEffect(() => {
    if (!breakExceeded || !breakEndTime) return undefined;
    const tick = () => setOverBySeconds(Math.max(0, Math.floor((Date.now() - breakEndTime) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [breakExceeded, breakEndTime]);

  // Same percent-remaining → color logic Header.tsx's own break capsule
  // already uses (breakCapsuleColor) — green above half, amber at 25-50%,
  // red at or below a quarter left — reapplied here as the icon ring's
  // stroke instead of a plain always-warning tint.
  const percentRemaining = useMemo(() => {
    const totalSeconds = Number(breakDuration) * 60;
    if (!totalSeconds || countdown == null) return 100;
    return Math.max(0, Math.min(100, (countdown / totalSeconds) * 100));
  }, [breakDuration, countdown]);
  const ringTone = breakExceeded
    ? 'destructive'
    : percentRemaining <= 25
      ? 'destructive'
      : percentRemaining <= 50
        ? 'warning'
        : 'success';
  const RING_TONE_CLASS = {
    success: 'bg-success/15 text-success',
    warning: 'bg-warning/15 text-warning',
    destructive: 'bg-destructive/15 text-destructive',
  };
  const RING_PING_CLASS = {
    success: 'bg-success/30',
    warning: 'bg-warning/30',
    destructive: 'bg-destructive/30',
  };

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto p-4">
      <div className="w-full max-w-sm overflow-hidden rounded-3xl bg-card shadow-lg">
        <div
          className={`flex flex-col items-center gap-4 px-6 pb-7 pt-8 text-center ${
            breakExceeded
              ? 'bg-gradient-to-b from-destructive/10 to-transparent'
              : 'bg-gradient-to-b from-warning/10 to-transparent'
          }`}
        >
          <span className={`relative flex h-16 w-16 items-center justify-center rounded-full ${RING_TONE_CLASS[ringTone]}`}>
            {!breakExceeded ? (
              <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${RING_PING_CLASS[ringTone]}`} />
            ) : null}
            <Coffee className="relative h-7 w-7" />
          </span>

          <div>
            <div className="text-[15px] font-semibold text-foreground">You're on a break</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {breakTitle || 'Break'} — you're offline for calls until you end it
            </div>
            {breakDescription && (
              <div className="mt-1 max-w-[260px] text-[11px] text-muted-foreground/80">
                {breakDescription}
              </div>
            )}
          </div>

          <div
            className={`num text-[40px] font-bold leading-none tracking-tight tabular-nums ${
              breakExceeded ? 'text-destructive' : 'text-foreground'
            }`}
          >
            {breakExceeded ? formatClock(overBySeconds) : formatClock(countdown)}
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {breakExceeded ? 'Over time' : 'Time remaining'}
          </div>
        </div>

        <div className={`border-t ${HAIRLINE} px-6 py-4`}>
          <Button
            variant={breakExceeded ? 'destructive' : 'outline'}
            className="w-full gap-2"
            disabled={ending}
            onClick={onEndBreak}
          >
            <LogOut className="h-4 w-4" />
            {ending ? 'Ending…' : 'End Break'}
          </Button>
        </div>
      </div>
    </div>
  );
}
